import { Button, Tooltip, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { CheckOutlined, ClockCircleOutlined, CopyOutlined, SafetyCertificateOutlined } from "@ant-design/icons";

import http from "../../http";
import Timer from "../../components/Timer/Timer";

const OTP_TTL_SECONDS = 120;
const BRAND = '#377EF3';
const DANGER = '#DC000C';
const DIGIT_FONT = "'SFMono-Regular', Menlo, Consolas, monospace";

const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins + ':' + String(secs).padStart(2, '0');
};

const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
    }
    // Fallback for non-secure contexts (plain http deployments)
    return new Promise((resolve, reject) => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
            document.execCommand('copy') ? resolve() : reject(new Error('copy rejected'));
        } catch (error) {
            reject(error);
        } finally {
            document.body.removeChild(textarea);
        }
    });
};

const OTPPage = () => {

    const [otp, setOTP] = useState(null);
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const expiryTimeoutRef = useRef(null);
    const copiedTimeoutRef = useRef(null);

    // Clear pending timeouts so they never fire on an unmounted component
    useEffect(() => () => {
        clearTimeout(expiryTimeoutRef.current);
        clearTimeout(copiedTimeoutRef.current);
    }, []);

    const handleGenerateOTP = () => {
        setLoading(true);
        let url = `/api/v1/auth/otp/generate_otp/`;
        http.post(url)
            .then(response => {
                if (response.status === 200) {
                    setOTP(response.data.otp);
                    setCopied(false);
                    clearTimeout(expiryTimeoutRef.current);
                    expiryTimeoutRef.current = setTimeout(() => {
                        setOTP(null);
                    }, OTP_TTL_SECONDS * 1000);
                }
                else {
                    message.error("Error Generating OTP");
                }
            })
            .catch(error => {
                message.error("Error Generating OTP");
            })
            .finally(() => setLoading(false));
    };

    const handleCopy = () => {
        copyToClipboard(String(otp))
            .then(() => {
                setCopied(true);
                clearTimeout(copiedTimeoutRef.current);
                copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => message.error("Unable to copy the code"));
    };

    const digits = otp ? String(otp).split('') : [];

    return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 16px' }}>
            <div style={{
                width: '100%',
                maxWidth: 420,
                padding: 28,
                textAlign: 'center',
                background: '#fff',
                border: '1px solid #eaedf2',
                borderRadius: 14,
                boxShadow: '0 2px 12px rgba(16, 24, 40, 0.06)'
            }}>
                <div style={{
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 1.2,
                    textTransform: 'uppercase',
                    color: '#8c93a1'
                }}>
                    One Time Password
                </div>

                {otp ? (
                    <>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 8,
                            margin: '20px 0 18px'
                        }}>
                            {digits.map((digit, index) => (
                                <div key={index} style={{
                                    minWidth: 46,
                                    height: 60,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 28,
                                    fontWeight: 600,
                                    fontFamily: DIGIT_FONT,
                                    color: '#1a1f2b',
                                    background: '#f6f8fc',
                                    border: '1px solid #e4e9f2',
                                    borderRadius: 10
                                }}>
                                    {digit}
                                </div>
                            ))}
                        </div>

                        <Timer maxTime={OTP_TTL_SECONDS}>
                            {(secondsLeft) => (
                                <div style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '4px 12px',
                                    fontSize: 13,
                                    borderRadius: 999,
                                    color: secondsLeft <= 30 ? DANGER : '#6b7280',
                                    background: secondsLeft <= 30 ? 'rgba(220, 0, 12, 0.08)' : '#f3f4f6'
                                }}>
                                    <ClockCircleOutlined />
                                    Expires in {formatTime(secondsLeft)}
                                </div>
                            )}
                        </Timer>

                        <Tooltip title={copied ? 'Copied' : 'Copy code to clipboard'}>
                            <Button
                                type="primary"
                                size="large"
                                block
                                icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                                onClick={handleCopy}
                                style={{
                                    marginTop: 20,
                                    background: copied ? '#22a06b' : BRAND,
                                    borderColor: copied ? '#22a06b' : BRAND
                                }}
                            >
                                {copied ? 'Copied' : 'Copy Code'}
                            </Button>
                        </Tooltip>
                    </>
                ) : (
                    <>
                        <SafetyCertificateOutlined style={{
                            fontSize: 36,
                            color: '#c3cbdb',
                            margin: '22px 0 12px'
                        }} />
                        <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
                            Generate a code valid for 2 minutes.
                        </div>
                        <Button
                            type="primary"
                            size="large"
                            block
                            loading={loading}
                            onClick={handleGenerateOTP}
                            style={{ background: BRAND, borderColor: BRAND }}
                        >
                            Generate OTP
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default OTPPage;
